import { useEffect, useState } from 'react';
import type { HealthResponse } from '@mlc/shared';
import { fetchHealth } from './api';

/**
 * Version servie par le serveur, et détection d'une mise à jour déployée
 * pendant que l'application est ouverte (US-38).
 *
 * Le déploiement est devenu automatique : un `git push` met le VPS à jour sans
 * que personne ne s'en aperçoive. Or l'application vit surtout sur un
 * téléphone posé dans la cuisine, resté ouvert des heures. Le service worker
 * sert le réseau d'abord (`client/public/sw.js`), donc un rechargement suffit
 * à passer à la nouvelle version — encore faut-il savoir qu'il y a lieu de
 * recharger. C'est tout l'objet de ce module.
 */

/**
 * Un quart d'heure. Assez court pour qu'un déploiement ne passe pas la soirée
 * inaperçu, assez long pour ne pas transformer une sonde de vie en trafic de
 * fond. Le vrai déclencheur utile est le retour de veille, ci-dessous.
 */
const INTERVALLE_MS = 15 * 60 * 1000;

/**
 * Empreinte git du code en train de s'exécuter, cuite dans le bundle à la
 * construction (Dockerfile). C'est la seule référence fiable, et il a fallu
 * une deuxième version de ce module pour s'en rendre compte : prendre pour
 * référence la première réponse du serveur échoue précisément dans le cas
 * qu'on veut couvrir. Si ce premier appel tombe pendant le redémarrage du
 * déploiement, la première réponse obtenue est déjà celle du NOUVEAU serveur
 * — et l'onglet garderait l'ancien code sans jamais rien signaler.
 *
 * L'empreinte git plutôt que le numéro de version : tous les déploiements ne
 * changent pas `package.json`, alors qu'ils changent tous de commit.
 */
const EMPREINTE_EMBARQUEE = (import.meta.env.VITE_APP_COMMIT ?? '').trim();

export interface EtatVersion {
  /** Ce que le serveur annonce. `null` tant qu'il n'a pas répondu. */
  sante: HealthResponse | null;
  /** Le serveur sert désormais autre chose que ce qui tourne dans cet onglet. */
  miseAJourDisponible: boolean;
}

export function useVersion(): EtatVersion {
  const [sante, setSante] = useState<HealthResponse | null>(null);
  const [miseAJourDisponible, setMiseAJourDisponible] = useState(false);

  useEffect(() => {
    // Repli quand l'empreinte n'a pas été injectée — développement, ou image
    // construite à la main : la première empreinte observée fait référence,
    // avec la réserve décrite plus haut.
    let empreinteDeRepli: string | null = null;
    let abandonne = false;

    function verifier(): void {
      fetchHealth()
        .then((reponse) => {
          if (abandonne) return;
          setSante(reponse);
          // Sans session, la sonde ne dit pas quel commit tourne
          // (`server/src/routes/health.ts`) : il n'y a rien à comparer, et
          // surtout rien à conclure. Comparer le numéro de version à la place
          // ferait apparaître le bandeau au moment de la connexion, quand la
          // réponse cesse d'être anonyme — un faux positif à chaque ouverture.
          if (reponse.commit === undefined) return;
          if (EMPREINTE_EMBARQUEE !== '') {
            if (reponse.commit !== EMPREINTE_EMBARQUEE) setMiseAJourDisponible(true);
            return;
          }
          if (empreinteDeRepli === null) {
            empreinteDeRepli = reponse.commit;
            return;
          }
          if (reponse.commit !== empreinteDeRepli) setMiseAJourDisponible(true);
        })
        .catch(() => {
          // Hors ligne, ou serveur en train de redémarrer pendant son propre
          // déploiement : silence, on retentera. Une erreur affichée ici
          // ferait paniquer pour la seule chose qui se passe normalement.
        });
    }

    verifier();
    const minuterie = setInterval(verifier, INTERVALLE_MS);

    // Le cas réel : le téléphone était en veille pendant le déploiement. À son
    // réveil on veut la réponse tout de suite, pas au prochain quart d'heure.
    function auRetour(): void {
      if (document.visibilityState === 'visible') verifier();
    }
    document.addEventListener('visibilitychange', auRetour);

    return () => {
      abandonne = true;
      clearInterval(minuterie);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, []);

  return { sante, miseAJourDisponible };
}

/**
 * « 10 août 2026 à 14:32 ». Une date de construction illisible ne vaut pas
 * mieux qu'une date absente : en cas de doute sur ce que le serveur a renvoyé,
 * on n'affiche rien plutôt qu'un « Invalid Date ».
 */
export function formaterDateConstruction(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' }).format(date);
}
